import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnDestroy, Output, PLATFORM_ID } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

export type AstralTab = {
  id: string;
  label?: string;
  labelKey?: string;
  labelParams?: Record<string, unknown>;
  disabled?: boolean;
};

export type AstralTabsAppearance = 'default' | 'flat' | 'tinted';

@Component({
  selector: 'astral-tabs',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    <div class="astral-tabs" [class.astral-tabs--content-sized]="!fill" [class.astral-tabs--flat]="appearance === 'flat'" [class.astral-tabs--tinted]="appearance === 'tinted'" [class.astral-tabs--two-tabs]="tabs.length === 2" role="tablist" [attr.aria-label]="ariaLabel | translate">
      @for (tab of tabs; track tab.id) {
        <button
          type="button"
          class="astral-tabs__tab"
          role="tab"
          [id]="getTabId(tab)"
          [class.astral-tabs__tab--active]="tab.id === selectedId"
          [attr.aria-controls]="panelId || null"
          [attr.aria-selected]="tab.id === selectedId"
          [attr.tabindex]="tab.id === selectedId ? 0 : -1"
          [disabled]="disabled || tab.disabled"
          (click)="selectTab(tab)"
          (keydown)="onKeydown($event, tab)"
        >
          {{ (tab.labelKey ?? tab.label ?? '') | translate: tab.labelParams }}
        </button>
      }
    </div>
  `,
  host: {
    '[class.astral-tabs-host--sticky]': 'sticky',
  },
  styleUrls: ['./astral-tabs.component.scss'],
})
export class AstralTabsComponent implements AfterViewInit, OnDestroy {
  private static nextId = 0;
  private readonly uid = `astral-tabs-${++AstralTabsComponent.nextId}`;
  private stickyHeader?: HTMLElement;
  private stickyHeaderObserver?: ResizeObserver;
  private stickyStateFrame?: number;
  private isStickyStuck = false;

  @Input() ariaLabel = 'COMMON.TABS';
  @Input() appearance: AstralTabsAppearance = 'default';
  @Input() disabled = false;
  @Input() fill = true;
  @Input() panelId?: string;
  @Input() selectedId = '';
  @Input() sticky = false;
  @Input() tabIdPrefix?: string;
  @Input() tabs: readonly AstralTab[] = [];

  @Output() selectedIdChange = new EventEmitter<string>();

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  ngAfterViewInit(): void {
    if (!this.sticky || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.stickyHeader = this.findPrecedingFormHeader();
    if (!this.stickyHeader) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.updateStickyOffset();
      this.stickyHeaderObserver = new ResizeObserver(() => {
        this.updateStickyOffset();
        this.scheduleStickyStateUpdate();
      });
      this.stickyHeaderObserver.observe(this.stickyHeader!);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.updateStickyState();
    });
  }

  ngOnDestroy(): void {
    this.stickyHeaderObserver?.disconnect();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('scroll', this.onScroll);
      if (this.stickyStateFrame !== undefined) {
        cancelAnimationFrame(this.stickyStateFrame);
      }
    }
  }

  getTabId(tab: AstralTab): string {
    return `${this.tabIdPrefix ?? this.uid}-${tab.id}`;
  }

  selectTab(tab: AstralTab): void {
    if (this.disabled || tab.disabled) {
      return;
    }

    if (tab.id !== this.selectedId) {
      this.selectedId = tab.id;
    }
    this.selectedIdChange.emit(tab.id);
  }

  onKeydown(event: KeyboardEvent, currentTab: AstralTab): void {
    const enabledTabs = this.tabs.filter(tab => !tab.disabled);
    const currentIndex = enabledTabs.findIndex(tab => tab.id === currentTab.id);
    if (currentIndex < 0 || !enabledTabs.length) {
      return;
    }

    let nextIndex: number | null = null;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % enabledTabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = enabledTabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = enabledTabs[nextIndex];
    this.selectTab(nextTab);
    document.getElementById(this.getTabId(nextTab))?.focus();
  }

  private findPrecedingFormHeader(): HTMLElement | undefined {
    let element: HTMLElement | null = this.host.nativeElement;
    while (element?.parentElement) {
      for (let sibling = element.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
        if (sibling.tagName === 'APP-FORM-HEADER') {
          return sibling as HTMLElement;
        }
      }
      element = element.parentElement;
    }

    return undefined;
  }

  private updateStickyOffset(): void {
    if (!this.stickyHeader) {
      return;
    }

    this.host.nativeElement.style.setProperty('--astral-tabs-sticky-offset', `${this.stickyHeader.offsetHeight}px`);
  }

  private readonly onScroll = (): void => this.scheduleStickyStateUpdate();

  private scheduleStickyStateUpdate(): void {
    if (this.stickyStateFrame !== undefined) {
      return;
    }

    this.stickyStateFrame = requestAnimationFrame(() => {
      this.stickyStateFrame = undefined;
      this.updateStickyState();
    });
  }

  private updateStickyState(): void {
    if (!this.stickyHeader) {
      return;
    }

    const pageHasScrolled = window.scrollY > 0;
    const tabTop = this.host.nativeElement.getBoundingClientRect().top;
    const headerBottom = this.stickyHeader.getBoundingClientRect().bottom;
    const isStickyStuck = pageHasScrolled && tabTop <= headerBottom + 1;
    if (isStickyStuck === this.isStickyStuck) {
      return;
    }

    this.isStickyStuck = isStickyStuck;
    this.host.nativeElement.classList.toggle('astral-tabs-host--stuck', isStickyStuck);
  }
}
